import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { validateOnboardingCode } from '@/lib/codeGenerator'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { ArrowRight, UserCheck } from 'lucide-react'

export default function ArtistOnboardingPage() {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!code.trim()) {
      setError('Please enter your code')
      return
    }

    const upperCode = code.trim().toUpperCase()
    
    if (!validateOnboardingCode(upperCode)) {
      setError('Code must be 4 characters (letters and numbers)')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Find the onboarding document with this code
      const onboardingsRef = collection(db, 'onboardings')
      const q = query(
        onboardingsRef, 
        where('code', '==', upperCode),
        where('status', '==', 'ready_for_artist')
      )
      
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        setError('Invalid code or onboarding not ready. Please check your code.')
        return
      }

      const onboardingDoc = querySnapshot.docs[0]
      const onboardingId = onboardingDoc.id
      const onboardingData = onboardingDoc.data()
      
      // If Step 1 (Account) is already completed (userId exists), redirect to Step 2
      // This is determined by checking if userId field exists
      if (onboardingData.userId) {
        console.log('Step 1 already completed, redirecting to onboarding with step 2')
        // The flow page will handle redirecting to step 2 automatically
      }
      
      // Navigate to the onboarding flow with the document ID
      navigate(`/onboarding/${onboardingId}`)
      
    } catch (err) {
      console.error('Error finding onboarding:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 4)
    setCode(value)
    if (error) setError(null)
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <UserCheck className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold">Artist Onboarding</h1>
          <p className="text-muted-foreground">
            Welcome! Enter your onboarding code to continue setting up your artist profile.
          </p>
        </div>

        {/* Code Entry Form */}
        <Card>
          <CardHeader>
            <CardTitle>Enter Your Code</CardTitle>
            <CardDescription>
              You should have received a 4-character code from our team
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Onboarding Code</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="ABCD"
                  className="text-center text-lg font-mono uppercase tracking-wider"
                  maxLength={4}
                  autoComplete="off"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-center">
                  4 characters (letters and numbers)
                </p>
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full flex items-center justify-center gap-2"
                disabled={loading || code.length !== 4}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    Let's Go
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            Don't have a code yet? Contact our team to start your onboarding process.
          </p>
        </div>
      </div>
    </div>
  )
}
